import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { Authorizer } from "@visioni/a3s-access/authorizer";
import { TenantId } from "@visioni/a3s-schema";
import { UserId } from "@visioni/a3s-users";
import { myPermissionsQuery } from "../queries";

const STALE_TIME_MS = 5 * 60 * 1000;

export interface AuthorizerState {
  authorizer: Authorizer | undefined;
  userId: UserId | undefined;
  tenantId: TenantId | undefined;
  isTenantAdmin: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches the user's serialized casbin policies from /access/me
 * and instantiates an Authorizer for client-side permission checks.
 * Derives isTenantAdmin directly from the casbin model.
 *
 * The Authorizer is held in a ref rather than in React Query's cache
 * to preserve the class prototype (React Query's structural sharing
 * strips class methods from cached objects).
 */
export function useAuthorizer(): AuthorizerState {
  const apiConfig = useApiConfig();
  const {
    data,
    isLoading: isFetchLoading,
    isError: isFetchError,
  } = useQuery({
    ...myPermissionsQuery(apiConfig, {}),
    staleTime: STALE_TIME_MS,
    retry: 2,
  });

  const authorizerRef = useRef<Authorizer | undefined>(undefined);
  const [authorizerReady, setAuthorizerReady] = useState(false);
  const [authorizerError, setAuthorizerError] = useState(false);
  const lastSerializedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const serialized = data?.serializedPermissions;
    if (!serialized || serialized === lastSerializedRef.current) return;

    lastSerializedRef.current = serialized;
    setAuthorizerReady(false);
    setAuthorizerError(false);

    // fromSerializedPermissions is async; if `serialized` changes again before
    // this resolves, an older promise could resolve last and overwrite state
    // with stale data. Guard by checking that this call is still the latest.
    Authorizer.fromSerializedPermissions(serialized)
      .then((instance) => {
        if (lastSerializedRef.current !== serialized) return;
        authorizerRef.current = instance;
        setAuthorizerReady(true);
      })
      .catch(() => {
        if (lastSerializedRef.current !== serialized) return;
        authorizerRef.current = undefined;
        setAuthorizerError(true);
      });
  }, [data?.serializedPermissions]);

  const isLoading = isFetchLoading || (!!data && !authorizerReady && !authorizerError);
  const isError = isFetchError || authorizerError;

  return useMemo(() => {
    if (isLoading)
      return {
        authorizer: undefined,
        userId: undefined,
        tenantId: undefined,
        isTenantAdmin: false,
        isLoading: true,
        isError: false,
      };

    const authorizer = authorizerRef.current;
    if (isError || !data || !authorizer)
      return {
        authorizer: undefined,
        userId: undefined,
        tenantId: undefined,
        isTenantAdmin: false,
        isLoading: false,
        isError: true,
      };

    const userId = UserId.fromString(data.userId);
    const tenantId = TenantId.fromString(data.tenantId);

    return {
      authorizer,
      userId,
      tenantId,
      isTenantAdmin: authorizer.isTenantAdmin(tenantId, userId),
      isLoading: false,
      isError: false,
    };
  }, [data, isLoading, isError]);
}
